import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { Home, Plus, LayoutGrid, LogOut, LogIn, Building2, Heart, ShieldCheck, GraduationCap, Menu, X, Briefcase } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { logo_url, brand_name, brand_color } = useBranding();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  const links = [
    { to: "/", label: "Browse", icon: Home, testid: "nav-browse" },
    { to: "/saved", label: "Saved", icon: Heart, testid: "nav-saved" },
    { to: "/students", label: "Students", icon: GraduationCap, testid: "nav-students" },
    { to: "/agents", label: "Agents", icon: Briefcase, testid: "nav-agents" },
  ];

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 border-b border-border/60 bg-[#F7F5F0]/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl text-white" style={{ backgroundColor: brand_color }}>
            {logo_url ? (
              <img data-testid="platform-logo" src={fileUrl(logo_url)} alt={brand_name} className="h-full w-full object-cover" />
            ) : (
              <Building2 className="h-5 w-5" />
            )}
          </span>
          <span data-testid="brand-name" className="font-display text-xl font-semibold tracking-tight sm:text-2xl" style={{ color: brand_color }}>
            {brand_name}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-2 lg:flex">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}>
              <Button variant="ghost" className={`rounded-full text-[#2C3830] hover:bg-[#2C3830]/5 ${isActive(l.to) ? "bg-[#2C3830]/5" : ""}`}>
                <l.icon className="mr-1.5 h-4 w-4" /> {l.label}
              </Button>
            </Link>
          ))}
          <NotificationBell />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin">
                  <Button variant="ghost" className="rounded-full text-[#2C3830] hover:bg-[#2C3830]/5">
                    <ShieldCheck className="mr-1.5 h-4 w-4" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button variant="ghost" className="rounded-full text-[#2C3830] hover:bg-[#2C3830]/5">
                  <LayoutGrid className="mr-1.5 h-4 w-4" /> Dashboard
                </Button>
              </Link>
              <Link to="/create" data-testid="nav-create">
                <Button className="rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]">
                  <Plus className="mr-1.5 h-4 w-4" /> List Property
                </Button>
              </Link>
              <Button data-testid="nav-logout" onClick={handleLogout} variant="ghost" size="icon" className="rounded-full text-[#6B7280] hover:bg-[#2C3830]/5" title="Log out">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login">
              <Button className="rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]">
                <LogIn className="mr-1.5 h-4 w-4" /> Sign In
              </Button>
            </Link>
          )}
        </nav>

        {/* Mobile controls */}
        <div className="flex items-center gap-1 lg:hidden">
          <NotificationBell />
          <Button
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            variant="ghost"
            size="icon"
            className="rounded-full text-[#2C3830] hover:bg-[#2C3830]/5"
            aria-label="Menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div data-testid="nav-mobile-menu" className="border-t border-border/60 bg-[#F7F5F0] px-5 py-3 lg:hidden">
          <nav className="flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`${l.testid}-m`} onClick={() => setMenuOpen(false)}>
                <div className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-[#2C3830] ${isActive(l.to) ? "bg-[#2C3830]/5" : ""}`}>
                  <l.icon className="h-4 w-4" /> {l.label}
                </div>
              </Link>
            ))}
            <div className="my-2 h-px bg-border/60" />
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin-m" onClick={() => setMenuOpen(false)}>
                    <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-[#2C3830]"><ShieldCheck className="h-4 w-4" /> Admin</div>
                  </Link>
                )}
                <Link to="/dashboard" data-testid="nav-dashboard-m" onClick={() => setMenuOpen(false)}>
                  <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-[#2C3830]"><LayoutGrid className="h-4 w-4" /> Dashboard</div>
                </Link>
                <Link to="/create" data-testid="nav-create-m" onClick={() => setMenuOpen(false)}>
                  <Button className="mt-1 w-full rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]">
                    <Plus className="mr-1.5 h-4 w-4" /> List Property
                  </Button>
                </Link>
                <button data-testid="nav-logout-m" onClick={handleLogout} className="mt-1 flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[#6B7280]">
                  <LogOut className="h-4 w-4" /> Log out
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login-m" onClick={() => setMenuOpen(false)}>
                <Button className="w-full rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]">
                  <LogIn className="mr-1.5 h-4 w-4" /> Sign In
                </Button>
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
